import React, { useEffect, useMemo, useState } from "react";
import { humanizeKey, isUrl } from "../lib/normalize";

const SECTION_LABELS = {
	patients: "Patients",
	treatments: "Treatments",
	molecular_markers_normalized: "Molecular Markers Normalized",
	AEs_normalized: "Adverse Events Normalized",
	discontinuation_AEs_normalized: "Discontinuation Adverse Events",
};

const PREFIX_MAP = {
	patients: "patient_",
	treatments: "treatment_",
	molecular_markers_normalized: "marker_",
	AEs_normalized: "AE_",
	discontinuation_AEs_normalized: "discontinuation_AEs_normalized_",
};

const COLLAPSIBLE_SECTIONS = new Set([
	"molecular_markers_normalized",
	"AEs_normalized",
]);

const BADGE_KEYS = new Set(["is_MPO"]);

function isEmptyValue(value) {
	if (value === null || value === undefined) return true;
	if (typeof value === "string" && value.trim() === "") return true;
	if (Array.isArray(value)) {
		return value.length === 0 || value.every(isEmptyValue);
	}
	if (typeof value === "object") {
		const values = Object.values(value);
		return values.length === 0 || values.every(isEmptyValue);
	}
	return false;
}

function shouldHide(value, hideEmptyFields) {
	return hideEmptyFields && isEmptyValue(value);
}

function prettyPrintJSON(obj, hideEmptyFields, indent = 0, parentKey = "") {
	if (obj === null) return `<span class="json-null">null</span>`;
	if (typeof obj !== "object") {
		const val = typeof obj === "string" ? obj : String(obj);
		if (isUrl(val)) {
			return `<a href="${val}" target="_blank" rel="noopener noreferrer" class="json-link">${val}</a>`;
		}
		return `<span class="json-value">${val}</span>`;
	}

	return Object.entries(obj)
		.filter(([, value]) => !shouldHide(value, hideEmptyFields))
		.map(([key, value]) => {
			const isNested = typeof value === "object" && value !== null;
			const isArrayIndex = /^\d+$/.test(key);
			let indexLabel = null;

			if (isArrayIndex) {
				const index = Number(key) + 1;
				indexLabel = parentKey && PREFIX_MAP[parentKey]
					? `${PREFIX_MAP[parentKey]}${index}`
					: parentKey
						? `${parentKey}-${index}`
						: `item-${index}`;
			}

			if (isNested) {
				const label = isArrayIndex ? indexLabel : key;
				return `<div class="json-entry" style="margin-left:${indent * 24}px;">
					<span class="json-key">${label}</span><span class="json-colon">:</span>
				</div>${prettyPrintJSON(value, hideEmptyFields, indent + 1, label)}`;
			}

			const displayKey = isArrayIndex ? indexLabel : key;
			return `<div class="json-entry" style="margin-left:${indent * 24}px;">
				<span class="json-key">${displayKey}</span><span class="json-colon">:</span>
				<span class="json-value-wrapper">${prettyPrintJSON(value, hideEmptyFields, indent, displayKey || key)}</span>
			</div>`;
		})
		.join("");
}

function ValueBadge({ value, rawKey }) {
	if (value === null || value === undefined) {
		return <span className="detail-empty">—</span>;
	}
	if (typeof value === "boolean") {
		return (
			<span className={`detail-badge ${value ? "badge-yes" : "badge-no"}`}>
				{value ? "Yes" : "No"}
			</span>
		);
	}

	const stringValue = String(value);
	if (BADGE_KEYS.has(rawKey) && /^(yes|no)$/i.test(stringValue)) {
		const isYes = stringValue.toLowerCase() === "yes";
		return (
			<span className={`detail-badge ${isYes ? "badge-yes" : "badge-no"}`}>
				{stringValue}
			</span>
		);
	}
	if (isUrl(stringValue)) {
		return (
			<a href={stringValue} target="_blank" rel="noopener noreferrer" className="detail-link">
				{stringValue}
			</a>
		);
	}
	return <span className="detail-value">{stringValue}</span>;
}

function FieldRow({ label, value, rawKey }) {
	return (
		<div className="detail-field">
			<dt className="detail-label">{label}:</dt>
			<dd className="detail-data">
				<ValueBadge value={value} rawKey={rawKey} />
			</dd>
		</div>
	);
}

function ObjectCard({
	data,
	title,
	mpoValue,
	collapsible = true,
	sectionKey,
	hideEmptyFields,
}) {
	const [collapsed, setCollapsed] = useState(
		mpoValue !== undefined ? mpoValue !== "Yes" : false
	);

	if (!data || typeof data !== "object") return null;

	const entries = Object.entries(data).filter(([key, value]) => {
		if (mpoValue !== undefined && key === "is_MPO") return false;
		return !shouldHide(value, hideEmptyFields);
	});

	if (entries.length === 0 && hideEmptyFields) return null;

	const content = (
		<dl className={COLLAPSIBLE_SECTIONS.has(sectionKey) ? "detail-grid-two" : "detail-grid"}>
			{entries.map(([key, value]) =>
				typeof value === "object" && value !== null ? (
					<ManuscriptSection
						key={key}
						sectionKey={key}
						data={value}
						hideEmptyFields={hideEmptyFields}
					/>
				) : (
					<FieldRow
						key={key}
						label={humanizeKey(key)}
						value={value}
						rawKey={key}
					/>
				)
			)}
		</dl>
	);

	return (
		<div className="detail-card">
			{collapsible ? (
				<>
					<button
						className="detail-card-header"
						onClick={() => setCollapsed((current) => !current)}
						type="button"
					>
						<span className="detail-card-title">
							{title}
							{mpoValue !== undefined && (
								<>
									<span className="detail-card-sep"> — Molecularly guided: </span>
									<span className={`detail-badge ${mpoValue === "Yes" ? "badge-yes" : "badge-no"}`}>
										{mpoValue}
									</span>
								</>
							)}
						</span>
						<span className={`detail-chevron ${collapsed ? "" : "open"}`}>›</span>
					</button>
					{!collapsed && <div className="detail-card-body">{content}</div>}
				</>
			) : (
				<div className="detail-card-body no-margin-top">{content}</div>
			)}
		</div>
	);
}

function ManuscriptSection({ sectionKey, data, hideEmptyFields }) {
	const label = SECTION_LABELS[sectionKey] || humanizeKey(sectionKey);
	const singularLabel = label.replace(/s$/, "");
	const isArray = Array.isArray(data);
	const items = (isArray ? data : [data]).filter(
		(item) => !shouldHide(item, hideEmptyFields)
	);
	const isTreatments = sectionKey === "treatments";
	const isCollapsible = COLLAPSIBLE_SECTIONS.has(sectionKey);
	const [collapsed, setCollapsed] = useState(isCollapsible);

	if (items.length === 0 && hideEmptyFields) return null;

	return (
		<div className="detail-section">
			<div
				className={`detail-section-header ${isCollapsible ? "detail-card-header" : ""}`}
				onClick={isCollapsible ? () => setCollapsed((current) => !current) : undefined}
				style={isCollapsible ? { cursor: "pointer" } : undefined}
			>
				<span className="detail-section-title">{label}</span>
				{isArray && <span className="detail-count">{items.length}</span>}
				{isCollapsible && (
					<span className={`detail-chevron ${collapsed ? "" : "open"}`}>›</span>
				)}
			</div>

			{!collapsed && (
				<div className="detail-section-body">
					{items.map((item, index) => (
						<ObjectCard
							key={index}
							data={item}
							title={items.length > 1 ? `${singularLabel} ${index + 1}` : singularLabel}
							collapsible={!isCollapsible}
							mpoValue={isTreatments ? item?.is_MPO : undefined}
							sectionKey={sectionKey}
							hideEmptyFields={hideEmptyFields}
						/>
					))}
				</div>
			)}
		</div>
	);
}

function ManuscriptDetail({ manuscript, hideEmptyFields }) {
	const entries = Object.entries(manuscript).filter(
		([, value]) =>
			value !== null &&
			value !== undefined &&
			!shouldHide(value, hideEmptyFields)
	);
	const topLevel = entries.filter(([, value]) => typeof value !== "object");
	const sections = entries.filter(([, value]) => typeof value === "object");

	return (
		<div className="detail-root">
			{topLevel.length > 0 && (
				<dl className="detail-grid detail-grid-top">
					{topLevel.map(([key, value]) => (
						<FieldRow key={key} label={humanizeKey(key)} value={value} rawKey={key} />
					))}
				</dl>
			)}
			{sections.map(([key, value]) => (
				<ManuscriptSection
					key={key}
					sectionKey={key}
					data={value}
					hideEmptyFields={hideEmptyFields}
				/>
			))}
		</div>
	);
}

export default function ManuscriptDetailModal({
	open,
	onClose,
	manuscript,
	loading,
	hideEmptyFields = false,
}) {
	const [viewMode, setViewMode] = useState("detail");

	useEffect(() => {
		if (!open) return;
		const { overflow } = document.body.style;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = overflow;
		};
	}, [open]);

	const prettyHtml = useMemo(() => {
		if (!open || !manuscript || viewMode !== "json") return "";
		return prettyPrintJSON(manuscript, hideEmptyFields);
	}, [open, manuscript, viewMode, hideEmptyFields]);

	if (!open) return null;

	return (
		<div className="modal-backdrop" onClick={onClose}>
			<div className="modal" onClick={(event) => event.stopPropagation()}>
				<div className="modal-header">
					<strong>{manuscript?.title ?? "Loading…"}</strong>
					<div className="modal-header-actions">
						{!loading && manuscript && (
							<button
								className="chip"
								onClick={() => setViewMode((current) => current === "detail" ? "json" : "detail")}
							>
								{viewMode === "detail" ? "JSON View" : "Table View"}
							</button>
						)}
						<button className="chip" onClick={onClose}>Close</button>
					</div>
				</div>

				<div className="modal-body">
					{loading ? (
						<div className="pubs-loading">
							<div className="spinner" style={{ width: "14px", height: "14px" }} />
						</div>
					) : !manuscript ? (
						<div className="pubs-empty">
							<div className="empty-title" style={{ color: "#DC2626" }}>
								Failed to load manuscript
							</div>
						</div>
					) : viewMode === "detail" ? (
						<ManuscriptDetail manuscript={manuscript} hideEmptyFields={hideEmptyFields} />
					) : (
						<pre className="json-view" dangerouslySetInnerHTML={{ __html: prettyHtml }} />
					)}
				</div>
			</div>
		</div>
	);
}
